import { useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw, X } from "lucide-react";
import { SnakeGame } from "./SnakeGame";
import { Game2048 } from "./Game2048";
import { Button } from "./ui/button";

interface Props {
  open: boolean;
  /** Current stage caption, e.g. "Analyzing photo…" */
  stage?: string;
  /** Lower bound of usual range, in seconds. Default 30. */
  expectedMinSec?: number;
  /** Upper bound of usual range, in seconds. Default 60. */
  expectedMaxSec?: number;
  /** Seconds before we surface a "network looks slow" warning + Retry/Cancel.
   *  Default 90. Keep below the parent's hard timeout. */
  networkWarnSec?: number;
  /** When set, generation has failed — overlay shows error + Retry button. */
  errorMessage?: string | null;
  /** Optional retry handler. When provided, a Retry CTA appears once generation
   *  is over time OR when {@link errorMessage} is set. The parent owns credit
   *  rules — edge functions already refund on failure, so a retry simply
   *  re-attempts the original call. */
  onRetry?: () => void;
  /** Optional cancel handler. When provided, an X button appears so users can
   *  dismiss the overlay (e.g. after a failure they don't want to retry). */
  onCancel?: () => void;
}

const fmt = (s: number) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

/**
 * Full-screen loading overlay for any image-generation flow.
 * - Sticky header with stage + live timer + progress bar.
 * - Scrollable middle with a distraction game.
 * - Sticky bottom action bar that always stays reachable on Android,
 *   respecting `env(safe-area-inset-bottom)` so the OS gesture bar
 *   never overlaps the Earn Credits CTA or the Retry/Cancel buttons.
 */
export const GenerationOverlay = ({
  open,
  stage,
  expectedMinSec = 30,
  expectedMaxSec = 60,
  networkWarnSec = 90,
  errorMessage,
  onRetry,
  onCancel,
}: Props) => {
  const [elapsed, setElapsed] = useState(0);
  // Pick the game once per overlay opening.
  const Game = useMemo(() => (Math.random() < 0.5 ? "snake" : "2048"), [open]);

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [open]);

  if (!open) return null;

  const overTime = elapsed > expectedMaxSec;
  // After `networkWarnSec` we treat it as a likely network issue: surface the
  // warning copy AND show Retry/Cancel so the user isn't stuck staring at a
  // spinner. The parent owns refund + abort logic via onRetry/onCancel.
  const networkSlow = !errorMessage && elapsed >= networkWarnSec;
  const showRetry = !!onRetry && (!!errorMessage || networkSlow);
  const isFailed = !!errorMessage;

  // Ease the progress bar to ~90% across the expected window, then idle.
  const targetSec = Math.min(elapsed, expectedMaxSec * 0.9);
  const progress = isFailed ? 100 : Math.min(90, (targetSec / expectedMaxSec) * 100);

  return (
    // z-[60] so the overlay sits above any open Radix Dialog/Sheet (which use z-50).
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur-sm p-3 sm:p-4">
      <div
        className="flex w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-elevated"
        style={{
          maxHeight:
            "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1.5rem)",
        }}
      >
        {/* Sticky header — always visible so the user sees progress + timer */}
        <div
          className="shrink-0 border-b border-border/60 bg-card px-4 pt-4 pb-3"
          style={{ paddingTop: "calc(1rem + env(safe-area-inset-top, 0px))" }}
        >
          <div className="flex items-center gap-3">
            {isFailed ? (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
                <X className="h-3 w-3" />
              </span>
            ) : (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="truncate text-xs font-medium">
                {isFailed ? "Generation failed" : (stage ?? "Generating image…")}
              </p>
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={
                      isFailed
                        ? "h-full rounded-full bg-destructive transition-all duration-500 ease-out"
                        : "h-full rounded-full gradient-primary transition-all duration-500 ease-out"
                    }
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="shrink-0 tabular-nums text-[10px] text-muted-foreground">
                  {fmt(elapsed)}
                </p>
              </div>
            </div>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                aria-label="Dismiss"
                className="ml-1 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {isFailed ? (
            <p className="mt-1.5 text-[11px] text-destructive">
              {errorMessage}
            </p>
          ) : networkSlow ? (
            <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              Looks like the network is slow. Please check your connection and try again.
            </p>
          ) : overTime ? (
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Taking longer than usual… we'll open the result as soon as it's ready.
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Runs in the background — play while you wait. We'll open your result automatically.
            </p>
          )}
        </div>

        {/* Scrollable middle — the distraction game */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
          <div className="w-full">
            {Game === "snake" ? <SnakeGame /> : <Game2048 />}
          </div>
        </div>

        {/* Sticky bottom action bar — always reachable, respects Android safe area.
            Houses Earn Credits + Retry/Cancel so they never get hidden behind the
            OS gesture bar on phones with a bottom inset. */}
        <div
          className="shrink-0 border-t border-border/60 bg-card px-4 pt-3"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        >
          {showRetry && (
            <div className="mb-3 flex gap-2">
              <Button
                onClick={onRetry}
                className="h-10 flex-1 gradient-primary text-primary-foreground"
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                {isFailed ? "Retry generation" : "Retry"}
              </Button>
              {onCancel && (
                <Button onClick={onCancel} variant="outline" className="h-10">
                  {isFailed ? "Close" : "Cancel"}
                </Button>
              )}
            </div>
          )}
          {!showRetry && (
            <p className="mt-2 text-center text-[10px] text-muted-foreground">
              No charge if generation fails — credit auto-refunds.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
